import React, { useEffect, useState, useCallback } from "react";
import { Table, Input, Button, message, Popconfirm, Space, Tag, Select, Modal, Descriptions, Tabs, Col, Row, Avatar } from "antd";
import {
    ReloadOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    UserOutlined,
    FileExcelOutlined,
    PrinterOutlined,
    MailOutlined,
    PhoneOutlined,
    InfoCircleOutlined,
    HistoryOutlined,
    CloseCircleOutlined,
    CloseOutlined,
    CalendarOutlined,
    SearchOutlined,
    WalletOutlined,
    DollarOutlined,
} from "@ant-design/icons";
import { formatDate } from "../../util/dateUtils";
import Breadcrumbs from "../../component/Breadcrumbs";
import AddUser from "../../component/AddUser";
import { getImageUrl } from "../../component/SafeImage";
import UserServ from "../../service/UserServ";
import EmptyState from "../../util/EmptyState";
import { FilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import { PrimaryButton, ResetButton } from "../../component/ui/ActionButtons";
import DataTable from "../../component/ui/DataTable";
import FormSelect from "../../component/ui/FormSelect";
import TableActions from "../../component/ui/TableActions";
import DateRangePicker from "../../component/ui/DateRangePicker";
import QuickFilter from "../../component/ui/QuickFilter";
import dayjs from "dayjs";
import { useAdminTable } from "../hooks/useAdminTable";


const ALL_TABS = [
    { key: "all", label: "All" },
    { key: "seller", label: "Seller" },
    { key: "user", label: "Buyer" },
    { key: "admin", label: "Admin" },
];

const UsersPage = () => {
    // Modal
    const [userModal, setUserModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [userDetails, setUserDetails] = useState(null);
    const [viewUserModal, setViewUserModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [quickFilter, setQuickFilter] = useState(undefined);
    const userRole = localStorage.getItem("role"); // superadmin, admin, seller

    const availableTabs = ALL_TABS;

    const fetchUsers = useCallback(async (params) => {
        const query = {
            limit: params.limit,
            page: params.page
        };
        if (params.search) query.search = params.search;
        if (params.roleFilter && params.roleFilter !== 'all') query.role = params.roleFilter;
        
        if (params.isActiveFilter === 'active') query.isActive = true;
        else if (params.isActiveFilter === 'inactive') query.isActive = false;
        
        if (params.isBlockedFilter === 'blocked') query.isBlocked = true;
        else if (params.isBlockedFilter === 'unblocked') query.isBlocked = false;
        
        if (params.dateRange?.[0]) query.startDate = params.dateRange[0].startOf('day').toISOString();
        if (params.dateRange?.[1]) query.endDate = params.dateRange[1].endOf('day').toISOString();

        const res = await UserServ.index(query);
        return { data: res?.data || [], total: res?.total || 0 };
    }, []);

    const {
        data: users,
        setData: setUsers,
        total: totalUsers,
        loading,
        params,
        setParams,
        handleSearch,
        refresh
    } = useAdminTable(fetchUsers, "admin_users_list", {
        page: 1, limit: 10, search: "", roleFilter: "all", isActiveFilter: "all", isBlockedFilter: "all", dateRange: null
    });

    // Default to 'all' tab if current roleFilter is not in available tabs
    useEffect(() => {
        if (!availableTabs.find(tab => tab.key === params.roleFilter)) {
            setParams(prev => ({ ...prev, roleFilter: "all", page: 1 }));
        }
    }, [availableTabs, params.roleFilter]);

    // ================= ACTIONS =================
    const handleAddUser = () => {
        setUserDetails(null);
        setIsEditing(false);
        setUserModal(true);
    };

    const handleEdit = (record) => {
        setUserDetails(record);
        setIsEditing(true);
        setUserModal(true);
    };

    const deleteUser = async (id) => {
        try {
            await UserServ.remove(id);
            message.success("User removed");
            refresh();
        } catch (err) {
            message.error("Remove failed");
        }
    };

    const refreshUsers = (isRefresh) => {
        setUserModal(false);
        if (isRefresh) refresh();
    };

    const handleViewUser = (user) => {
        setSelectedUser(user);
        setViewUserModal(true);
    };

    const sortedData = [...users].sort((a, b) => {
        const isASuperAdmin = a.role?.toLowerCase() === "superadmin";
        const isBSuperAdmin = b.role?.toLowerCase() === "superadmin";

        if (isASuperAdmin && !isBSuperAdmin) return -1;
        if (!isASuperAdmin && isBSuperAdmin) return 1;

        return 0;
    });

    // ================= TABLE COLUMNS =================
    const columns = [
        {
            title: "Image",
            key: "image",
            width: 80,
            align: "center",
            render: (_, record) => {
                const img = record.profilePhoto || record.profileImage || record.image;
                return (
                    <Avatar
                        size={36}
                        src={img ? getImageUrl(img) : null}
                        icon={<UserOutlined style={{ color: '#64748b' }} />}
                        style={{
                            borderRadius: 10,
                            border: '1px solid #e2e8f0',
                            background: '#f1f5f9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    />
                );
            },
        },
        {
            title: "Full Name",
            key: "fullName",
            render: (_, record) => (
                <span style={{ color: "#1a1d2e", fontWeight: 600 }}>
                    {`${record.firstName || ''} ${record.lastName || ''}`.trim()}
                </span>
            ),
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
        },
        {
            title: "Phone",
            dataIndex: "phone",
            key: "phone",
        },
        {
            title: "Role",
            dataIndex: "role",
            key: "role",
            // Hide only when specifically on the Buyer tab
            hidden: params.roleFilter === "user",
            render: (role) => {
                const displayRole = role || "user";
                const roleColors = {
                    user: "blue",
                    seller: "green",
                    admin: "purple",
                    superadmin: "red",
                };

                return (
                    <Tag color={roleColors[displayRole] || "default"}>
                        {displayRole.toUpperCase()}
                    </Tag>
                );
            },
        },
        {
            title: "Status",
            dataIndex: "isActive",
            key: "isActive",
            align: "center",
            width: 100,
            render: (isActive, record) => {
                if (record.isBlocked) {
                    return <Tag color="error">BLOCKED</Tag>;
                }
                return (
                    <Tag color={isActive ? "success" : "warning"}>
                        {isActive ? "ACTIVE" : "INACTIVE"}
                    </Tag>
                );
            },
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            width: 100,
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleViewUser(record);
                    }}
                    style={{
                        borderRadius: 3,
                        fontWeight: 600,
                        background: '#ffffffff',
                        color: '#004754ff',
                        border: '1px solid #e4e4e7',
                        boxShadow: '0 0 0 1px #e4e4e7',
                        height: 'auto',
                        padding: '0px 8px',
                        fontSize: 13,
                    }}
                >
                    View
                </Button>
            ),
        },
    ];

    const fetchAllUsers = async () => {
        try {
            // Fetch all users for export
            const res = await UserServ.index({ limit: 10000 });
            return Array.isArray(res?.data) ? res.data : [];
        } catch (error) {
            console.error("Error fetching all users:", error);
            message.error("Failed to fetch full user list");
            return [];
        }
    };

    // ================= RENDER =================
    return (
        <div className="pcu-padding-24">
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs />
            </div>

            <div className="sd-container" style={{ padding: '24px' }}>
                {/* Header matching Image 2 style */}
                <div className="sd-header" style={{ marginBottom: '24px', background: 'transparent', padding: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div className="sd-orders-icon-wrap" style={{ width: '48px', height: '48px', background: '#fff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                            <UserOutlined style={{ fontSize: '22px', color: '#4776E6' }} />
                        </div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' }}>Users</h1>
                            <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af', fontWeight: 500 }}>{totalUsers} users registered</p>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: '8px' }}>
                            <TableActions
                                onFetchFullData={fetchAllUsers}
                                columns={columns}
                                fileName="users_list"
                                pageName="Users"
                            // Use these for custom trigger icons if needed or just keep the defaults if they fit
                            />
                            {/* Adding the styled action buttons for Excel/Print consistency if TableActions doesn't provide them nicely */}
                            {/* <button className="ss-action-btn-circle" style={{ background: '#166534', color: '#fff' }} title="Export to Excel"><FileExcelOutlined /></button>
                            <button className="ss-action-btn-circle" style={{ background: '#475569', color: '#fff' }} title="Print List"><PrinterOutlined /></button> */}
                        </div>
                        <Button
                            type="primary"
                            onClick={handleAddUser}
                            style={{
                                height: '40px',
                                borderRadius: '10px',
                                background: 'linear-gradient(135deg, #FF8800 0%, #FF5E00 100%)',
                                border: 'none',
                                padding: '0 20px',
                                fontSize: '13px',
                                fontWeight: 700,
                                boxShadow: '0 4px 12px rgba(255, 136, 0, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <PlusOutlined style={{ fontSize: '16px' }} />
                            Add User
                        </Button>
                    </div>
                </div>

                {/* Status Tabs matching Image 2 */}
                <div style={{ marginBottom: '24px' }}>
                    <Tabs
                        activeKey={params.roleFilter}
                        onChange={(key) => setParams(prev => ({ ...prev, roleFilter: key, page: 1 }))}
                        items={availableTabs.map(tab => ({
                            key: tab.key,
                            label: tab.label,
                        }))}
                        className="premium-tabs-minimal"
                    />
                </div>

                <div className="pcu-padding-0">
                    {/* Row 1: 5 Filters matching Image 2 */}
                    <div className="ss-filter-bar" style={{ background: 'transparent', border: 'none', padding: '0 0 16px 0', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
                        <div className="ss-filter-field">
                            <span className="ss-filter-label" style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px' }}>SEARCH USER</span>
                            <div className="ss-search-wrap">
                                <SearchOutlined className="ss-search-icon" style={{ color: '#94a3b8' }} />
                                <input
                                    className="ss-search-input"
                                    placeholder="Search name, email, ..."
                                    value={params.search}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    style={{ height: '38px', borderRadius: '10px', fontSize: '13px' }}
                                />
                            </div>
                        </div>

                        <div className="ss-filter-field">
                            <span className="ss-filter-label" style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px' }}>STATUS</span>
                            <Select
                                placeholder="All Status"
                                style={{ width: '100%', height: '38px' }}
                                onChange={(val) => setParams(prev => ({ ...prev, isActiveFilter: val, page: 1 }))}
                                value={params.isActiveFilter}
                                className="pcu-radius-10"
                            >
                                <Select.Option value="all">All Status</Select.Option>
                                <Select.Option value="active">Active</Select.Option>
                                <Select.Option value="inactive">Inactive</Select.Option>
                            </Select>
                        </div>

                        <div className="ss-filter-field">
                            <span className="ss-filter-label" style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px' }}>BLOCK STATUS</span>
                            <Select
                                placeholder="All Block Status"
                                style={{ width: '100%', height: '38px' }}
                                onChange={(val) => setParams(prev => ({ ...prev, isBlockedFilter: val, page: 1 }))}
                                value={params.isBlockedFilter}
                                className="pcu-radius-10"
                            >
                                <Select.Option value="all">All Block Status</Select.Option>
                                <Select.Option value="blocked">Blocked</Select.Option>
                                <Select.Option value="unblocked">Not Blocked</Select.Option>
                            </Select>
                        </div>

                        <div className="ss-filter-field">
                            <QuickFilter
                                value={quickFilter}
                                onChange={(key, range) => {
                                    setQuickFilter(key);
                                    setParams(prev => ({ ...prev, dateRange: range, page: 1 }));
                                }}
                            />
                        </div>

                        <div className="ss-filter-field">
                            <span className="ss-filter-label" style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px' }}>JOINED RANGE</span>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <div style={{ flex: 1 }}>
                                    <DateRangePicker
                                        value={params.dateRange}
                                        onChange={(val) => {
                                            setParams(prev => ({ ...prev, dateRange: val, page: 1 }));
                                            setQuickFilter(undefined);
                                        }}
                                        showLabel={false}
                                        style={{ height: '38px', borderRadius: '10px' }}
                                    />
                                </div>
                                <button
                                    className="ss-reset-btn"
                                    onClick={() => {
                                        setParams({ page: 1, limit: 10, search: "", roleFilter: "all", isActiveFilter: "all", isBlockedFilter: "all", dateRange: null });
                                        setQuickFilter(undefined);
                                        refresh();
                                    }}
                                    style={{
                                        height: '38px',
                                        width: '38px',
                                        padding: '0',
                                        borderRadius: '10px',
                                        background: '#fff',
                                        border: '1px solid #e2e8f0',
                                        color: '#64748b',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer'
                                    }}
                                    title="Reset Filters"
                                >
                                    <ReloadOutlined style={{ fontSize: '14px' }} />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="ss-table-wrap pcu-margin-top-16">
                        <DataTable
                            columns={columns}
                            data={sortedData}
                            rowKey="_id"
                            rowClassName={(record) => {
                                const role = record.role?.toLowerCase() || "user";
                                if (role === "superadmin") return "row-highlight-rejected"; // soft red
                                if (role === "seller") return "row-highlight-approved"; // soft green
                                if (role === "user") return "row-highlight-active-order"; // soft indigo
                                if (role === "admin") return "row-highlight-pending-approval"; // soft amber
                                return "";
                            }}
                            onRow={(record) => ({
                                style: { cursor: 'default' }
                            })}
                            locale={{
                                emptyText: (
                                    <div className="pcu-padding-y-60">
                                        <EmptyState
                                            title="No Users Found"
                                            description={(params.search || params.roleFilter !== "seller" || params.isActiveFilter !== "all" || params.isBlockedFilter !== "all") ? "No users match your filters." : "No users found in the system."}
                                            gradient="green"
                                            onAction={!(params.search || params.roleFilter !== "seller" || params.isActiveFilter !== "all" || params.isBlockedFilter !== "all") ? handleAddUser : undefined}
                                            actionText="Add User"
                                            icon="inbox"
                                        />
                                    </div>
                                )
                            }}
                            loading={loading}
                            pagination={{
                                current: params.page,
                                pageSize: params.limit,
                                total: totalUsers,
                                onChange: (p) => {
                                    setParams(prev => ({ ...prev, page: p }));
                                    const pageContent = document.querySelector('.page-content');
                                    if (pageContent) pageContent.scrollTo({ top: 0, behavior: 'smooth' });
                                    else window.scrollTo({ top: 0, behavior: 'smooth' });
                                },
                                onShowSizeChange: (_, size) => setParams(prev => ({ ...prev, limit: size, page: 1 }))
                            }}
                        />
                    </div>
                </div>

                {/* User Details Modal */}
                <Modal
                    open={viewUserModal}
                    onCancel={() => setViewUserModal(false)}
                    footer={null}
                    width={600}
                    centered
                    closable={false}
                    bodyStyle={{ padding: 0 }}
                    maskStyle={{ backdropFilter: 'blur(3px)' }}
                    style={{ borderRadius: 20, overflow: 'hidden' }}
                >
                    {selectedUser && (
                        <div style={{ background: '#fff', borderRadius: 20, overflow: 'hidden' }}>
                            {/* Header Section - Premium Style */}
                            <div style={{
                                padding: '16px 20px',
                                background: 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)',
                                borderBottom: '1px solid #e2e8f0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 14,
                                position: 'relative'
                            }}>
                                <button
                                    onClick={() => setViewUserModal(false)}
                                    style={{ position: 'absolute', right: 16, top: 16, background: '#fff', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}
                                >
                                    <CloseOutlined />
                                </button>

                                <div style={{ width: 56, height: 56, borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0', flexShrink: 0, background: '#f8fafc', boxShadow: '0 4px 10px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Avatar
                                        size={50}
                                        src={(selectedUser.profilePhoto || selectedUser.profileImage) ? getImageUrl(selectedUser.profilePhoto || selectedUser.profileImage) : null}
                                        icon={<UserOutlined style={{ color: '#4f46e5' }} />}
                                        style={{ borderRadius: 8, background: 'transparent' }}
                                    />
                                </div>

                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>{`${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim()}</h2>
                                        <Tag color={selectedUser.isBlocked ? "error" : (selectedUser.isActive ? "success" : "warning")} style={{ margin: 0, borderRadius: 5, padding: '0 8px', fontSize: 9, fontWeight: 700, border: 'none', background: '#fff', color: selectedUser.isBlocked ? '#ef4444' : (selectedUser.isActive ? '#16a34a' : '#f59e0b'), boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                            {(selectedUser.isBlocked ? "BLOCKED" : (selectedUser.isActive ? "ACTIVE" : "INACTIVE"))}
                                        </Tag>
                                    </div>
                                    <p style={{ margin: '1px 0 0', fontSize: 11, color: '#64748b', fontWeight: 600 }}>ID: <span style={{ color: '#4f46e5' }}>{selectedUser.userId || selectedUser._id?.substring(0, 12).toUpperCase()}</span> • {selectedUser.role?.toUpperCase() || 'USER'}</p>
                                </div>
                            </div>

                            {/* Detail Grid Section - Compact */}
                            <div style={{ padding: '14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, background: '#fff' }}>
                                {/* Email - Full Width */}
                                <div style={{ gridColumn: '1 / -1', background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                    <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2, letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 4 }}><MailOutlined /> Email Address</div>
                                    <div style={{ fontSize: 12, color: '#334155', fontWeight: 600, lineHeight: 1.3 }}>{selectedUser.email}</div>
                                </div>

                                {/* Phone & Role */}
                                <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                    <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}><PhoneOutlined /> Phone Number</div>
                                    <div style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>{selectedUser.phone || 'N/A'}</div>
                                </div>
                                <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                    <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}><UserOutlined /> Account Role</div>
                                    <div style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>{selectedUser.role?.toUpperCase() || 'USER'}</div>
                                </div>

                                {/* User ID Full width */}
                                <div style={{ gridColumn: '1 / -1', background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                    <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}><InfoCircleOutlined /> User ID</div>
                                    <div style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>{selectedUser.userId || selectedUser._id}</div>
                                </div>

                                {/* Dates */}
                                <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                    <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}><CalendarOutlined /> Joined Date</div>
                                    <div style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>
                                        {new Date(selectedUser.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>
                                <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', border: '1px solid #f1f5f9' }}>
                                    <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}><ReloadOutlined /> Last Updated</div>
                                    <div style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>
                                        {new Date(selectedUser.updatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>

                                {/* Financial Info */}
                                <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '6px 12px', border: '1px solid #bcf0da' }}>
                                    <div style={{ fontSize: 9, fontWeight: 800, color: '#16a34a', textTransform: 'uppercase', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}><DollarOutlined /> Wallet Balance</div>
                                    <div style={{ fontSize: 14, color: '#166534', fontWeight: 800 }}>₹{selectedUser.walletBalance || 0}</div>
                                </div>
                                <div style={{ background: '#eff6ff', borderRadius: 8, padding: '6px 12px', border: '1px solid #bfdbfe' }}>
                                    <div style={{ fontSize: 9, fontWeight: 800, color: '#3b82f6', textTransform: 'uppercase', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}><WalletOutlined /> User Credits</div>
                                    <div style={{ fontSize: 14, color: '#1e40af', fontWeight: 800 }}>{selectedUser.credits || 0}</div>
                                </div>
                            </div>

                            {/* Footer Section - Compact */}
                            <div style={{ padding: '12px 16px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button
                                        style={{
                                            background: 'none', border: '1px solid #e2e8f0', color: '#64748b', fontSize: 12, fontWeight: 700,
                                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6
                                        }}
                                    >
                                        <HistoryOutlined /> History
                                    </button>
                                    <button
                                        style={{
                                            background: '#eff6ff', border: 'none', color: '#3b82f6', fontSize: 12, fontWeight: 700,
                                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6
                                        }}
                                        onClick={() => {
                                            handleEdit(selectedUser);
                                            setViewUserModal(false);
                                        }}
                                    >
                                        <EditOutlined /> Edit
                                    </button>
                                    <button
                                        style={{
                                            background: 'none', border: '1px solid #fef2f2', color: '#ef4444', fontSize: 12, fontWeight: 700,
                                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6
                                        }}
                                        onClick={() => {
                                            deleteUser(selectedUser._id);
                                            setViewUserModal(false);
                                        }}
                                    >
                                        <DeleteOutlined /> Remove
                                    </button>
                                </div>

                                <button
                                    onClick={() => setViewUserModal(false)}
                                    style={{
                                        padding: '6px 20px', borderRadius: 8, border: '1px solid #e2e8f0',
                                        background: '#f8fafc', color: '#64748b', fontSize: 12, fontWeight: 700,
                                        cursor: 'pointer', transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                                    onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    )}
                </Modal>

                {/* Add/Edit Modal */}
                {userModal && (
                    <AddUser
                        open={userModal}
                        userDetails={userDetails}
                        isEditing={isEditing}
                        refreshUsers={refreshUsers}
                    />
                )}
            </div>
        </div>
    );
};

export default UsersPage;

